import { Skeleton } from "@mui/material";
import useTitanApp from "@/hooks/useTitanApp";

export default function AnalyticsSkeleton() {
  const { appState } = useTitanApp();
  const { darkMode } = appState;
  const isDarkMode = darkMode;

  const skeletonSx = { bgcolor: isDarkMode ? "grey.800" : "" };
  const rows = Array(5).fill(null);

  return (
    <div className="p-6 space-y-6">
      {/* Back Button & Title */}
      <div className="space-y-4">
        <Skeleton
          animation="wave"
          variant="rectangular"
          width={140}
          height={20}
          className="rounded"
          sx={skeletonSx}
        />
        <Skeleton
          animation="wave"
          variant="rectangular"
          width={200}
          height={32}
          className="rounded"
          sx={skeletonSx}
        />
      </div>

      {/* Stats Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          "Total Consultations",
          "Total Revenue",
          "Avg Duration",
          "Avg Compliance",
        ].map((_, idx) => (
          <div
            key={idx}
            className={`p-4 rounded-lg border ${
              isDarkMode
                ? "border-gray-700 bg-gray-800"
                : "border-gray-200 bg-white"
            }`}
          >
            <Skeleton
              animation="wave"
              variant="text"
              width="70%"
              height={16}
              sx={skeletonSx}
              className="mb-2"
            />
            <Skeleton
              animation="wave"
              variant="rectangular"
              width="50%"
              height={36}
              sx={skeletonSx}
            />
          </div>
        ))}
      </div>

      {/* Consultations Section */}
      <div className="space-y-4">
        <div>
          <Skeleton
            animation="wave"
            variant="text"
            width={150}
            height={24}
            sx={skeletonSx}
            className="mb-1"
          />
          <Skeleton
            animation="wave"
            variant="text"
            width={220}
            height={16}
            sx={skeletonSx}
          />
        </div>

        {/* Table */}
        <div
          className={`border rounded-lg overflow-hidden ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          {/* Table Header */}
          <div
            className={`grid grid-cols-12 gap-4 p-4 font-medium text-sm ${
              isDarkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-gray-50 border-gray-200"
            } border-b`}
          >
            <div className="col-span-2">
              <Skeleton
                animation="wave"
                variant="text"
                width="80%"
                height={18}
                sx={skeletonSx}
              />
            </div>
            <div className="col-span-2">
              <Skeleton
                animation="wave"
                variant="text"
                width="70%"
                height={18}
                sx={skeletonSx}
              />
            </div>
            <div className="col-span-2">
              <Skeleton
                animation="wave"
                variant="text"
                width="60%"
                height={18}
                sx={skeletonSx}
              />
            </div>
            <div className="col-span-2">
              <Skeleton
                animation="wave"
                variant="text"
                width="50%"
                height={18}
                sx={skeletonSx}
              />
            </div>
            <div className="col-span-2">
              <Skeleton
                animation="wave"
                variant="text"
                width="65%"
                height={18}
                sx={skeletonSx}
              />
            </div>
            <div className="col-span-2">
              <Skeleton
                animation="wave"
                variant="text"
                width="90%"
                height={18}
                sx={skeletonSx}
              />
            </div>
          </div>

          {/* Table Rows */}
          {rows.map((_, index) => (
            <div
              key={index}
              className={`grid grid-cols-12 gap-4 items-center p-4 border-b ${
                isDarkMode
                  ? "border-gray-700 bg-gray-900"
                  : "border-gray-200 bg-white"
              }`}
            >
              {/* Patient Name */}
              <div className="col-span-2">
                <Skeleton
                  animation="wave"
                  variant="text"
                  width="85%"
                  height={20}
                  sx={skeletonSx}
                />
              </div>

              {/* Practitioner */}
              <div className="col-span-2">
                <Skeleton
                  animation="wave"
                  variant="text"
                  width="75%"
                  height={20}
                  sx={skeletonSx}
                />
              </div>

              {/* Date */}
              <div className="col-span-2">
                <Skeleton
                  animation="wave"
                  variant="text"
                  width="70%"
                  height={20}
                  sx={skeletonSx}
                />
              </div>

              {/* Time */}
              <div className="col-span-2">
                <Skeleton
                  animation="wave"
                  variant="text"
                  width="55%"
                  height={20}
                  sx={skeletonSx}
                />
              </div>

              {/* Duration */}
              <div className="col-span-2">
                <Skeleton
                  animation="wave"
                  variant="text"
                  width="60%"
                  height={20}
                  sx={skeletonSx}
                />
              </div>

              {/* Compliance Rating */}
              <div className="col-span-2 flex justify-end">
                <Skeleton
                  animation="wave"
                  variant="text"
                  width="45%"
                  height={20}
                  sx={skeletonSx}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
